// Pure, stateless helpers and shared constants extracted from useAgentStore.js.
// Nothing here closes over the store's `set`/`get` or over module-level mutable
// state (timers, registries) — those live in agentStreamLifecycle.js. Keeping
// these side-effect-free helpers in their own module shrinks the store file and
// lets the per-concern store slices share them without circular imports.
//
// Naming convention:
//   * `_`-prefixed identifiers belong to the agent-store's internal domain
//     vocabulary — session/stream status predicates, activity-patch builders,
//     optimistic-id minting, and the two store-state accessors. They presume
//     the agent-store's model and are consumed only by the store slices.
//   * Unprefixed exports are domain-agnostic IO/transform utilities (HTTP
//     payload parsing, error-message resolution, mime inference) that could
//     live in any module.
//   * SCREAMING_SNAKE_CASE names are shared status constants.

import useProjectStore from "../ProjectCanvas/useProjectStore";

export function _getClientId() {
  return useProjectStore.getState().clientId;
}

export const STATUS_WAITING_FOR_REVIEW = "WAITING_FOR_REVIEW";
export const STATUS_SESSION_UNDER_REVIEW = "SESSION_UNDER_REVIEW";
export const STATUS_IN_PROGRESS = "IN_PROGRESS";
export const STATUS_WAITING_FOR_WORKERS = "WAITING_FOR_WORKERS";
export const STATUS_WAITING_FOR_SCHEDULED_MESSAGE = "WAITING_FOR_SCHEDULED_MESSAGE";
export const STATUS_WAITING_FOR_FORM_SUBMISSION = "WAITING_FOR_FORM_SUBMISSION";
export const STATUS_CANCELLING = "CANCELLING";
export const DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE =
  "This conversation is currently under review. Please wait for the reviewer to finish.";

export function parseMaybeJsonPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch (_error) {
      return null;
    }
  }
  if (typeof payload === "object") {
    return payload;
  }
  return null;
}

export function normalizeSessionStatusReason(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function extractApiStatusCode(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return 0;
  }
  return Number(
    responseOrError?.status || responseOrError?.response?.status || 0
  );
}

export function extractApiPayload(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return null;
  }
  const rawPayload = responseOrError?.data ?? responseOrError?.response?.data;
  return parseMaybeJsonPayload(rawPayload);
}

export function resolveApiErrorMessage(responseOrError, fallbackMessage) {
  const payload = extractApiPayload(responseOrError);
  if (payload && typeof payload === "object") {
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error.trim();
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
  }
  if (
    typeof responseOrError?.message === "string" &&
    responseOrError.message.trim()
  ) {
    return responseOrError.message.trim();
  }
  return fallbackMessage;
}

// Resolves the user-facing error string for an agent create/update mutation,
// from either the resolved error object send_request returns (no throw) or a
// thrown error. Prefers the server's `message` (suffixed with its `errorId`
// when present), then a generic "Server returned <status>" line, then the
// raw error message, then the supplied fallback.
export function resolveAgentMutationError(responseOrError, fallbackMessage) {
  const serverMessage = responseOrError?.response?.data?.message;
  const errorId = responseOrError?.response?.data?.errorId;
  const status = responseOrError?.response?.status;
  if (serverMessage) {
    return `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`;
  }
  if (status) {
    return `Server returned ${status}. Please try again.`;
  }
  return responseOrError?.message || fallbackMessage;
}

export function inferMimeTypeFromFileName(fileName) {
  if (typeof fileName !== "string") return "text/plain";
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".md")) return "text/markdown";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".csv")) return "text/csv";
  return "text/plain";
}

export function _extractBlockedIngressPayload(responseOrError) {
  const statusCode = extractApiStatusCode(responseOrError);
  if (statusCode !== 409) {
    return null;
  }
  const payload = extractApiPayload(responseOrError);
  if (!payload || payload.status !== STATUS_SESSION_UNDER_REVIEW) {
    return null;
  }
  return {
    chatSessionId: String(payload.chatSessionId || ""),
    sessionStatus: String(payload.sessionStatus || STATUS_WAITING_FOR_REVIEW),
    message:
      typeof payload.message === "string" && payload.message.trim()
        ? payload.message.trim()
        : DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE,
  };
}

// Builds the sessionDetail patch for a 409 blocked-while-review response: keep
// the currently-viewed detail when it matches, else seed a minimal stub, then
// stamp the under-review status + message and clear any prior statusReason.
// The surrounding chatStatus/error/return handling differs per caller (send vs
// takeover, viewed vs not), so only this shared shape lives here.
export function _buildBlockedIngressDetailPatch(sessionDetail, chatSessionId, blockedPayload) {
  const detail =
    sessionDetail?.chatSessionId === chatSessionId
      ? sessionDetail
      : { chatSessionId, messages: [] };
  return {
    ...detail,
    status: blockedPayload.sessionStatus,
    blockedWhileReviewMessage: blockedPayload.message,
    statusReason: null,
  };
}

export const _TERMINAL_WORKER_STATUSES = new Set(["COMPLETED", "CANCELLED", "ERROR"]);
export const _STREAMABLE_SESSION_STATUSES = new Set([
  STATUS_IN_PROGRESS,
  STATUS_WAITING_FOR_WORKERS,
  STATUS_WAITING_FOR_SCHEDULED_MESSAGE,
  STATUS_WAITING_FOR_FORM_SUBMISSION,
  STATUS_WAITING_FOR_REVIEW,
  // 409 blocked-ingress flows (_extractBlockedIngressPayload) produce SESSION_UNDER_REVIEW
  // as a non-terminal "under review" state; treat it as streamable so SSE reconnect/processing
  // continues until the session leaves review.
  STATUS_SESSION_UNDER_REVIEW,
]);

// Subset of streamable statuses where the agent (or its workers) is actively executing.
// Waiting-on-external-input states stay streamable for SSE but must not drive the DM-sidebar
// "thinking" dots — same rule as chatStatus not entering streaming on those snapshots.
export const _SIDEBAR_ACTIVE_SESSION_STATUSES = new Set([
  STATUS_IN_PROGRESS,
  STATUS_WAITING_FOR_WORKERS,
]);

export const _ACTIVE_RUN_EVENT_TYPES = new Set([
  "tool.invoked",
  "tool.completed",
  "tool.failed",
  "tool_activity",
  "worker_status_changed",
]);

export function _isStreamableSessionStatus(status) {
  return !status || _STREAMABLE_SESSION_STATUSES.has(status);
}

export function _isSidebarActiveSessionStatus(status) {
  return Boolean(status) && _SIDEBAR_ACTIVE_SESSION_STATUSES.has(status);
}

export function _parseSessionActivityMs(session) {
  const raw = session?.updatedAt ?? session?.createdAt;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function _resolveSessionTeamId(session) {
  return session?.teamId || session?.scopedTeamId || null;
}

export function _buildAgentActivityFromSessions(sessions) {
  const activeMap = {};
  const lastActivityMap = {};
  const teamLastActivityMap = {};
  for (const session of sessions) {
    const updatedAtMs = _parseSessionActivityMs(session);
    const teamId = _resolveSessionTeamId(session);
    if (teamId && updatedAtMs > 0) {
      const prevTeam = teamLastActivityMap[teamId] || 0;
      if (updatedAtMs > prevTeam) {
        teamLastActivityMap[teamId] = updatedAtMs;
      }
    }
    if (!session?.agentId) continue;
    if (updatedAtMs > 0) {
      const prev = lastActivityMap[session.agentId] || 0;
      if (updatedAtMs > prev) {
        lastActivityMap[session.agentId] = updatedAtMs;
      }
    }
    if (_isSidebarActiveSessionStatus(session.status)) {
      activeMap[session.agentId] = true;
    }
  }
  return { activeMap, lastActivityMap, teamLastActivityMap };
}

export function _bumpTeamLastActivityPatch(state, teamId, timestampMs = Date.now()) {
  const prev = state.teamLastActivityAt?.[teamId] || 0;
  const next = Math.max(prev, timestampMs);
  if (next === prev) return null;
  return {
    teamLastActivityAt: { ...state.teamLastActivityAt, [teamId]: next },
  };
}

export function _activityMsFromPayload(payload) {
  const inner = payload?.payload ?? payload;
  const raw = inner?.updatedAt ?? inner?.timestamp;
  if (raw) {
    const ms = new Date(raw).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return Date.now();
}

export function _bumpAgentLastActivityPatch(state, agentId, timestampMs = Date.now()) {
  const prev = state.agentLastActivityAt?.[agentId] || 0;
  const next = Math.max(prev, timestampMs);
  if (next === prev) return null;
  return {
    agentLastActivityAt: { ...state.agentLastActivityAt, [agentId]: next },
  };
}

export function _isTerminalForStreaming(status) {
  return Boolean(status) && !_isStreamableSessionStatus(status) && status !== STATUS_CANCELLING;
}

export function _isKnownStreamableSessionStatus(status) {
  return Boolean(status) && _isStreamableSessionStatus(status);
}

export function _isActivelyRunningSessionStatus(status) {
  return status === STATUS_IN_PROGRESS;
}

export const _BUSY_CHAT_STATUSES = new Set(["submitted", "streaming", "error"]);

// Single rule for "a freshly observed terminal backend status must clear a busy
// chatStatus". session_complete is the normal clear, but it is unrecoverable
// once missed (the backend purges its replay buffer), so REST/poll fetches and
// mid-stream snapshots funnel their terminal observations through this same rule
// as the backstop against a permanently stuck "Thinking". Returns the reason
// string to record before clearing to 'ready', or null when no clear is
// warranted.
export function _terminalClearReason(observedStatus, chatStatus, source) {
  if (!_isTerminalForStreaming(observedStatus)) return null;
  if (!_BUSY_CHAT_STATUSES.has(chatStatus)) return null;
  return `${source}:terminal=${observedStatus}`;
}

export function _hasIncompleteWorkers(workerStatuses) {
  if (!workerStatuses) return false;
  return Object.values(workerStatuses).some(
    (w) => w?.status && !_TERMINAL_WORKER_STATUSES.has(w.status)
  );
}

// True while any team member node is still running, so the parent SSE stream is
// kept alive across team turns even when the parent session's own status is idle.
export function _hasRunningTeamActivity(teamActivityByRun) {
  if (!teamActivityByRun) return false;
  return Object.values(teamActivityByRun).some((n) => n?.status === "running");
}

// A team (orchestration parent) session, identified by either its sessionRole or its source.
// Team turns project the final visible message onto the parent transcript synchronously before
// session_complete, so they reconcile differently from single-agent turns.
export const _isTeamSessionDetail = (sd) =>
  !!sd && (sd.sessionRole === 'team' || sd.source === 'TEAM');

export function _isEmptyAssistantStreamMessage(msg) {
  if (!msg || msg.role === "user" || msg.role === "system") return false;
  const content = typeof msg.content === "string" ? msg.content.trim() : "";
  const hasParts = Array.isArray(msg.parts) && msg.parts.length > 0;
  const hasStreamingReasoning =
    Array.isArray(msg.parts) &&
    msg.parts.some((part) => part.type === "reasoning" && part.state === "streaming");
  const hasToolCalls = Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0;
  return !content && !hasParts && !hasStreamingReasoning && !hasToolCalls;
}

let _optimisticIdCounter = 0;
export function _generateOptimisticId() {
  return `_optimistic_${Date.now()}_${++_optimisticIdCounter}`;
}

export function _getLocalUserDisplayName() {
  const { userDisplayName, userName } = useProjectStore.getState();
  return userDisplayName || userName || undefined;
}

// Force a message's `.id` to equal the app message id (`messageId`), not the
// Mongo `_id` that a persisted copy arrives with. The live stream reducer and
// stream controller find the in-flight turn by `.id`, so keeping `.id` =
// messageId upholds the "live and persisted copies share one id" invariant: a
// server snapshot/replace can't orphan the turn the reducer is appending to.
// Centralizes the normalization that was previously re-asserted in each store
// action that ingests server messages.
export function _withAppMessageId(msg) {
  if (!msg) return msg;
  const appId = msg.messageId || msg.id;
  if (!appId) return msg;
  return { ...msg, messageId: appId, id: appId };
}
